import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../../components/form-control/Input';
import Button from '../../../components/form-control/Button';
import { editPolicyPayment } from '../../../api/requests';
import { PAYMENT_CHANNELS, PAYMENT_PROCESSORS } from '../../../resources/enums';
import { getSelectOptionsFromEnum, showToast, } from '../../../utils/miscFunctions';

const OFFLINE_PAYMENT_CHANNEL_OPTIONS = [
  { value: 'BANK', name: 'Bank' },
  { value: 'MOBILE_MONEY', name: 'Mobile Money' },
  { value: 'CASH', name: 'Cash' }
];

const OFFLINE_PROCESSOR_OPTIONS_BY_CHANNEL: Record<string, { value: string; name: string }[]> = {
  BANK: [
    { value: 'CARD', name: 'Card' },
    { value: 'BANK_TRANSFER', name: 'Bank Transfer' },
    { value: 'AUTO_DEBIT', name: 'Auto-Debit' }
  ],
  MOBILE_MONEY: [
    { value: 'MTN', name: 'MTN' },
    { value: 'AIRTEL_TIGO', name: 'Airtel' },
    { value: 'TELECEL', name: 'Vodafone' },
    { value: 'PAYSTACK', name: 'Paystack' },
    { value: 'HUBTEL', name: 'Hubtel' }
  ]
};
import DatePickerInput from '../../../components/form-control/DatePickerInput';
import moment from 'moment';
import { BANKS_OF_GHANA, INPUT_DATE_FORMAT } from '../../../resources/constants';
import { EyeIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import TextArea from '../../../components/form-control/TextArea';
import FileUpload from '../../../components/elements/FileUpload/FileUpload';
import useImage from '../../../hooks/imageHook';
import { IRequestResponse } from '../../../resources/interfaces';
import ToolTip from '../../../components/ToolTip';
import { Receipt, Calendar } from 'lucide-react';
import SelectDropdown from '../../../components/form-control/SelectDropdown';

interface IEditPayment {
  onProceed: () => void;
  onClose: () => void;
  payment: any;
}
export interface IEditPaymentDTO {
  reference: string;
  channel: string;
  processor: string;
  bank?: string;
  narration: string;
  dateOfPayment: string | Date | null;
  timeOfPayment: string | Date | null;
  dueDate: string | Date | null;
}

const PaymentEditModal = ({ onProceed, payment, onClose }: IEditPayment) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentEvidence, setPaymentEvidence] = useState<File | null>(payment.evidenceUrl || null);
  const { getImageFormData } = useImage();

  const handlePaymentEvidencePreview = () => {
    window.open(payment.evidenceUrl, '_blank');
  };

  return (
    <div className="w-full space-y-6 ">
      {/* Header Section */}

      <Formik
        initialValues={{
          reference: payment?.reference,
          channel: payment?.channel,
          processor: payment?.processor,
          bank: payment?.bank,
          narration: payment?.narration || '',
          dateOfPayment: payment?.dateOfPayment ? new Date(payment.dateOfPayment) : null,
          dueDate: payment?.dueDate ? new Date(payment.dueDate) : null
        }}
        validateOnChange
        validateOnBlur
        validationSchema={Yup.object().shape({
          channel: Yup.string().required('Channel is required').nullable(),
          processor: Yup.string().when('channel', {
            is: (channel: string) => channel && channel !== 'CASH',
            then: (processor) => processor.required('Payment process is required').nullable(),
            otherwise: (processor) => processor.notRequired().nullable()
          }),
          reference: Yup.string().required('Reference is required').nullable(),
          bank: Yup.string().when('processor', {
            is: (processor: string) => processor === 'BANK_TRANSFER' || processor === 'BANK',
            then: (bank) => bank.required('Bank is required').nullable(),
            otherwise: (bank) => bank.notRequired()
          }),
          // narration: Yup.string().required('Narration is required').nullable(),
          dueDate: Yup.date().required('Due date is required').nullable()
        })}
        onSubmit={(values) => {
          const payload: IEditPaymentDTO = {
            ...values,
            channel: payment?.channel ? payment?.channel : values.channel,
            dateOfPayment: values.dateOfPayment ? moment(values.dateOfPayment).format(INPUT_DATE_FORMAT) : null,
            timeOfPayment: values.dateOfPayment ? moment(values.dateOfPayment).format('HH:mm:ss') : null,
            dueDate: values.dueDate ? moment(values.dueDate).format(INPUT_DATE_FORMAT) : null
          };

          let evidenceFile;
          if (paymentEvidence) {
            evidenceFile = getImageFormData(paymentEvidence);
          }

          setLoading(true);
          editPolicyPayment(payment.id, payload, evidenceFile)
            .then((response: IRequestResponse) => {
              if (response) {
                showToast.success(response.message ?? 'Payment update request successful');
                onProceed();
              }
            })
            .catch((error: IRequestResponse) => {
              showToast.error(error.description);
            })
            .finally(() => {
              setLoading(false);
            });
        }}>
        {({ values, handleSubmit, handleChange, handleBlur, setFieldValue, touched, errors }) => (
          <form onSubmit={handleSubmit} className="space-y-6 px-3 py-2">
            <div className="grid grid-cols-1 gap-3 ">
              {/* Payment Information Card */}
              <div className="">
                <div className="bg-gray-50 px-3 py-6 rounded-lg dark:border-darkBg-border dark:bg-darkBg">

                  <div className="px-3 py-6 space-y-5">
                    <div className="grid grid-cols-2  gap-5">
                      <SelectDropdown
                        name="channel"
                        onChange={(value: string) => {
                          setFieldValue('channel', value);
                          setFieldValue('processor', '');
                          setFieldValue('bank', '');
                        }}
                        listSelectedValue={values.channel}
                        list={OFFLINE_PAYMENT_CHANNEL_OPTIONS}
                        onBlur={handleBlur}
                        label="Payment Channel"
                        required
                        placeholder="Select payment channel"
                        error={touched?.channel ? errors?.channel : undefined}
                        helperText={touched?.channel ? errors?.channel : undefined}
                      />

                      {values?.channel && values.channel !== 'CASH' && (
                        <SelectDropdown
                          name="processor"
                          list={OFFLINE_PROCESSOR_OPTIONS_BY_CHANNEL[values.channel] ?? []}
                          listSelectedValue={values.processor}
                          onBlur={handleBlur}
                          onChange={(value: string) => {
                            setFieldValue('processor', value);
                          }}
                          label="Payment Process"
                          placeholder="Select payment process"
                          required
                          error={touched?.processor ? errors?.processor : undefined}
                          helperText={touched?.processor ? errors?.processor : undefined}
                        />
                      )}

                    </div>

                    {(values.processor === 'BANK_TRANSFER' || values.processor === PAYMENT_PROCESSORS.BANK) && (
                      <div className="animate-in slide-in-from-top-2 duration-200">
                        <SelectDropdown
                          name="bank"
                          list={getSelectOptionsFromEnum(BANKS_OF_GHANA)}
                          listSelectedValue={values.bank}
                          onBlur={handleBlur}
                          onChange={(value: string) => {
                            setFieldValue('bank', value);
                          }}
                          label="Bank Name"
                          placeholder="Select bank"
                          required
                          error={touched?.bank ? errors?.bank : undefined}
                          helperText={touched?.bank ? errors?.bank : undefined}
                        />
                      </div>
                    )}

                    <Input
                      name="reference"
                      label={values.channel === PAYMENT_CHANNELS.BANK_TRANSFER ? "Reference" : "Receipt Number"}
                      type="text"
                      value={values.reference}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={values.channel === PAYMENT_CHANNELS.BANK_TRANSFER ? "Enter Reference" : "Enter Receipt Number"}
                      required
                      error={touched.reference ? errors.reference : undefined}
                      helperText={touched.reference ? errors.reference : undefined}
                    />

                    <div className="relative">
                      <DatePickerInput
                        id={'dateOfPayment'}
                        selected={values.dateOfPayment}
                        onChange={(date: Date | null) => {
                          setFieldValue('dateOfPayment', date);
                        }}
                        label="Payment Date & Time"
                        classes="w-full bg-gray-50 border-gray-200 focus:border-brolly-green focus:ring-brolly-green/20"
                        showTimeSelect
                        showYearDropdown
                        showMonthDropdown
                        allowFutureSelections={false}
                        dateFormat="dd-MM-yyyy hh:mm a"
                        required
                        error={touched?.dateOfPayment && errors?.dateOfPayment ? String(errors?.dateOfPayment) : undefined}
                        helperText={touched?.dateOfPayment && errors?.dateOfPayment ? String(errors?.dateOfPayment) : undefined}
                      />
                      <Calendar className="absolute right-3 top-9 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>

                    <div className="">
                      <TextArea
                        name={'narration'}
                        value={values.narration}
                        label="Narration / Reason for Edit"
                        placeholder="Please provide a reason for this payment edit request..."
                        onBlur={handleBlur}
                        onChange={handleChange}
                        autoComplete
                        rows={4}

                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Payment Evidence Card */}
              <div className="bg-white hidden rounded-2xl  border border-gray-100 dark:border-darkBg-border dark:bg-darkBg">
                <div className="rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-3 border-b border-amber-200 dark:border-darkBg-border dark:from-darkBg dark:to-darkBg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-darkBg-mainText">
                        Payment Evidence
                      </h3>
                    </div>
                    <ToolTip message="Upload payment receipt or transaction proof" placement='bottom'>
                      <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                    </ToolTip>
                  </div>
                </div>

                <div className="p-6">
                  <div className=" border-amber-300 bg-amber-50/50 rounded-xl text-center hover:bg-amber-50 transition-colors duration-200 dark:bg-darkBg">
                    <div className="flex flex-col items-center space-y-4">
                      <FileUpload
                        onFileLoad={(images: any) => {
                          if (images && images[0]) {
                            setPaymentEvidence(images[0]);
                          }
                        }}
                        showFileMax={true}
                        onFileDeleted={() => {
                          setPaymentEvidence(null);
                        }}
                        savedImage={paymentEvidence}
                        cropImage={false}
                        maxHeight={90}
                        maxWidth={200}
                        imageContainerHeight={'200px'}
                        imageAlt={'Payment Evidence'}
                      // required
                      />
                    </div>

                    {payment.evidenceUrl && (
                      <div className="mt-4 flex items-center justify-center gap-2 text-sm text-green-700 bg-green-50 px-4 py-2 rounded-lg">
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>File uploaded successfully</span>
                        <ToolTip message="Preview Payment Evidence">
                          <button
                            type="button"
                            onClick={handlePaymentEvidencePreview}
                            className="ml-2 text-brolly-green hover:text-brolly-green/80 transition-colors">
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </ToolTip>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200 dark:border-darkBg-border">
              <Button
                type="button"
                label="Cancel"
                onClick={onClose}
                bgColor="secondary"
                disabled={loading}
                fullWidth
              />
              <Button
                type="submit"
                label={loading ? 'Processing...' : 'Update Payment'}
                bgColor="primary"
                disabled={loading}
                loading={loading}
                fullWidth
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default PaymentEditModal;
